// react
import styled from 'styled-components';

import { FieldType } from '@karehero/models';

// components
import {
  Background,
  Button,
  Container,
  Loader,
  Title,
} from '@/components/atoms';
import { BackButton, MemphisHeader } from '@/components/molecules';
import { HorizontalSplit } from '@/components/templates';

import { EditorFields } from '@/components/organisms/EditorFields/EditorFields';
import { createFieldHelper } from '@/components/organisms/EditorFields/fieldHelper';

// theme
import theme from '@/theme';

interface RegisterInviteCodeProps {
  className?: string;
  value?: string;
  onChange?: (value: string) => void;
  isLoading?: boolean;
  isBackButton?: boolean;
}

const fields = createFieldHelper<{ code: string }>({
  code: {
    field: {
      type: FieldType.InviteCode,
    },
  },
});

/**
 * RegisterInviteCode is the page where the user can register with an invite code.
 */
export const RegisterInviteCode = ({
  value,
  onChange,
  isLoading,
  isBackButton = false,
}: RegisterInviteCodeProps) => {
  return (
    <HorizontalSplit
      topContent={
        <>
          {isBackButton && (
            <BackButtonWrapper>
              <BackButton />
            </BackButtonWrapper>
          )}
          <MemphisHeader backgroundColor={theme.color.cards.yellow.tertiary} />
        </>
      }
      bottomContent={
        <Background color={theme.color.cards.yellow.tertiary}>
          <Container size='sm'>
            <Wrapper>
              {isLoading ? (
                <Loader />
              ) : (
                <>
                  <Title size='sm' isNoMargin>
                    Enter your invite code
                  </Title>
                  <Description>
                    Head over to your email inbox to find the invite code.
                  </Description>
                  <EditorFields
                    fields={fields}
                    value={{ code: value }}
                    onChange={(val) => onChange?.(val.code)}
                    isFullWidth={false}
                  />
                  <div>
                    You can read our privacy policy{' '}
                    <Button
                      ariaLabel='privacy policy'
                      to='https://www.karehero.com/privacy-policy'
                      variant='link'
                      target='_blank'
                      isInline
                    >
                      here
                    </Button>
                  </div>
                </>
              )}
            </Wrapper>
          </Container>
        </Background>
      }
    />
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${(props) => props.theme.spacing[16]};
  padding: ${(props) => props.theme.spacing[32]} 0;
`;

const Description = styled.div`
  font: ${(props) => props.theme.typography.body.lg.regular};
  color: ${(props) => props.theme.color.neutral[90]};
  text-align: center;
`;

const BackButtonWrapper = styled.div`
  position: absolute;
  top: ${(props) => props.theme.spacing[16]};
  left: ${(props) => props.theme.spacing[16]};
  z-index: 1;
`;

RegisterInviteCode.displayName = 'RegisterInviteCode';

export default RegisterInviteCode;
